// src/pages/support/SupportUsersPage.tsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Users } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';
import { fieldControl } from '@/components/ui/fieldStyles';

interface UserRow {
  user_id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  created_at: number;
}

interface UserDetails extends UserRow {
  roles: string[];
  prepaid_balance: number;
  total_requests_30d: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const SupportUsersPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ users: UserRow[]; pagination: Pagination }>({
    queryKey: ['support-users', search, page],
    queryFn: () =>
      apiClient.get('/support/users', { params: { q: search || undefined, page, limit: 20 } }).then(r => r.data),
  });

  const { data: user, isFetching: loadingDetail } = useQuery<UserDetails>({
    queryKey: ['support-user', selectedUserId],
    queryFn: () => apiClient.get(`/support/users/${selectedUserId}`).then(r => r.data),
    enabled: !!selectedUserId,
  });

  const handleSearch = () => { setSearch(query); setPage(1); };

  const users = data?.users ?? [];

  return (
    <div className="space-y-6">
      <PageHeader title="User lookup" description="Read-only. No changes can be made from this view." />

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative sm:flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input
            type="text"
            aria-label="Search users"
            className={cn(fieldControl, 'pl-10')}
            placeholder="Search by email or name…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <Button className="h-11" leftIcon={<Search className="h-4 w-4" />} onClick={handleSearch}>
          Search
        </Button>
      </div>

      <Panel title="Users" bodyClassName="px-0 pb-2">
        {isLoading ? (
          <TableSkeleton rows={6} />
        ) : users.length === 0 ? (
          <EmptyState icon={<Users />} title="No users found." description={search ? 'Try a different email or name.' : undefined} />
        ) : (
          <Table minWidth={560}>
            <THead>
              <tr>
                <Th>Email</Th>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Joined</Th>
              </tr>
            </THead>
            <TBody>
              {users.map(u => (
                <Tr key={u.user_id} className="cursor-pointer" onClick={() => setSelectedUserId(u.user_id)}>
                  <Td>{u.email}</Td>
                  <Td>{u.full_name}</Td>
                  <Td>
                    <Badge tone={u.is_active ? 'success' : 'error'} dot>{u.is_active ? 'Active' : 'Suspended'}</Badge>
                  </Td>
                  <Td muted className="whitespace-nowrap">{new Date(u.created_at * 1000).toLocaleDateString()}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}

        {data?.pagination && data.pagination.pages > 1 && (
          <div className="flex flex-col gap-3 border-t border-border px-5 pt-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <span>Page <span className="font-mono tabular-nums">{data.pagination.page}</span> of <span className="font-mono tabular-nums">{data.pagination.pages}</span> (<span className="font-mono tabular-nums">{data.pagination.total}</span> total)</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={page >= data.pagination.pages} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Panel>

      <Modal isOpen={!!selectedUserId} onClose={() => setSelectedUserId(null)} title="User details" size="sm" className="w-full">
        {loadingDetail || !user ? (
          <SkeletonLines count={6} lineClassName="h-5" />
        ) : (
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-2.5 text-sm">
            <dt className="text-muted-foreground">Email</dt><dd className="break-all text-foreground">{user.email}</dd>
            <dt className="text-muted-foreground">Name</dt><dd className="text-foreground">{user.full_name}</dd>
            <dt className="text-muted-foreground">User ID</dt><dd className="break-all font-mono text-xs text-foreground">{user.user_id}</dd>
            <dt className="text-muted-foreground">Status</dt>
            <dd><Badge tone={user.is_active ? 'success' : 'error'} dot>{user.is_active ? 'Active' : 'Suspended'}</Badge></dd>
            <dt className="text-muted-foreground">Roles</dt><dd className="text-foreground">{user.roles.join(', ')}</dd>
            <dt className="text-muted-foreground">Balance</dt><dd className="font-mono tabular-nums text-foreground">{user.prepaid_balance.toLocaleString()} credits</dd>
            <dt className="text-muted-foreground">Requests (30 days)</dt><dd className="font-mono tabular-nums text-foreground">{user.total_requests_30d.toLocaleString()}</dd>
            <dt className="text-muted-foreground">Joined</dt><dd className="text-foreground">{new Date(user.created_at * 1000).toLocaleDateString()}</dd>
          </dl>
        )}
      </Modal>
    </div>
  );
};

export default SupportUsersPage;
